import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listKaraokePackagesAdmin,
  upsertKaraokePackage,
  deleteKaraokePackage,
  listKaraokeReservations,
  setKaraokeReservationStatus,
  type KaraokePackage,
} from "@/lib/karaoke.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mic, Plus, Trash2, Pencil } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/karaoke")({
  component: KaraokeAdmin,
});

type Draft = Omit<KaraokePackage, "id"> & { id?: string; includesText: string };

const EMPTY: Draft = {
  name: "",
  tagline: "",
  description: "",
  price_cents: 2000,
  price_unit: "per_person",
  min_guests: 6,
  max_guests: 30,
  duration_minutes: 120,
  includes: [],
  includesText: "",
  published: true,
  sort_order: 0,
};

function euros(cents: number) {
  return `€${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

function KaraokeAdmin() {
  const qc = useQueryClient();
  const packagesFn = useServerFn(listKaraokePackagesAdmin);
  const saveFn = useServerFn(upsertKaraokePackage);
  const deleteFn = useServerFn(deleteKaraokePackage);
  const bookingsFn = useServerFn(listKaraokeReservations);
  const statusFn = useServerFn(setKaraokeReservationStatus);

  const { data: packages = [] } = useQuery({
    queryKey: ["karaoke-packages-admin"],
    queryFn: () => packagesFn(),
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ["karaoke-reservations"],
    queryFn: () => bookingsFn(),
  });

  const [draft, setDraft] = useState<Draft | null>(null);

  const save = useMutation({
    mutationFn: async (d: Draft) =>
      saveFn({
        data: {
          ...(d.id ? { id: d.id } : {}),
          name: d.name,
          tagline: d.tagline,
          description: d.description,
          price_cents: d.price_cents,
          price_unit: d.price_unit as "per_person" | "per_group",
          min_guests: d.min_guests,
          max_guests: d.max_guests,
          duration_minutes: d.duration_minutes,
          includes: d.includesText
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean),
          published: d.published,
          sort_order: d.sort_order,
        },
      }),
    onSuccess: () => {
      toast.success("Package saved");
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["karaoke-packages-admin"] });
      qc.invalidateQueries({ queryKey: ["karaoke-packages"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Package deleted");
      qc.invalidateQueries({ queryKey: ["karaoke-packages-admin"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      statusFn({ data: { id, status } }),
    onSuccess: () => {
      toast.success("Booking updated");
      qc.invalidateQueries({ queryKey: ["karaoke-reservations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-10">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl">
          <Mic className="text-primary" /> Karaoke Terrace
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Karaoke bookings and the food & drinks packages offered to big groups.
        </p>
      </div>

      {/* Bookings */}
      <section>
        <h2 className="font-display text-xl">Karaoke bookings</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card/30">
          {bookings.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No karaoke bookings yet.</div>
          ) : (
            <table className="w-full min-w-[720px] text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Guest</th>
                  <th className="px-4 py-3">Party</th>
                  <th className="px-4 py-3">Notes / package</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {bookings.map((b) => {
                  const g = (b as { guests?: { full_name?: string; phone?: string; email?: string } }).guests;
                  return (
                    <tr key={b.id}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {new Date(b.starts_at).toLocaleString("en-IE", {
                          timeZone: "Europe/Dublin",
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                        <div className="text-xs text-muted-foreground">
                          {b.duration_minutes} min · {b.reference}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{g?.full_name ?? "Guest"}</div>
                        <div className="text-xs text-muted-foreground">{g?.phone}</div>
                      </td>
                      <td className="px-4 py-3">{b.party_size}</td>
                      <td className="max-w-[260px] px-4 py-3 text-xs text-muted-foreground">
                        {b.notes ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full border border-border px-2 py-0.5 text-xs uppercase tracking-wider">
                          {b.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatus.mutate({ id: b.id, status: "confirmed" })}
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setStatus.mutate({ id: b.id, status: "cancelled" })}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Packages */}
      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">Group packages</h2>
          <Button size="sm" onClick={() => setDraft({ ...EMPTY })}>
            <Plus size={15} /> New package
          </Button>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {packages.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card/30 p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-lg">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.tagline}</p>
                </div>
                <span className="text-sm text-primary">
                  {euros(p.price_cents)} {p.price_unit === "per_group" ? "/ group" : "pp"}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{p.description}</p>
              <p className="mt-2 text-xs">
                {p.min_guests}–{p.max_guests} guests · {p.duration_minutes} min ·{" "}
                {p.published ? "Published" : "Hidden"}
              </p>
              {p.includes.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  {p.includes.map((i) => (
                    <li key={i}>· {i}</li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDraft({ ...p, includesText: p.includes.join("\n") })}
                >
                  <Pencil size={14} /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(p.id)}>
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
            </div>
          ))}
          {packages.length === 0 && (
            <p className="text-sm text-muted-foreground">No packages yet — create the first one.</p>
          )}
        </div>

        {draft && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(draft);
            }}
            className="mt-6 grid gap-4 rounded-2xl border border-border bg-card/40 p-5 md:grid-cols-2"
          >
            <div className="md:col-span-2">
              <Label>Package name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Tagline</Label>
              <Input value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Price (€ cents)</Label>
              <Input
                type="number"
                min={0}
                value={draft.price_cents}
                onChange={(e) => setDraft({ ...draft, price_cents: +e.target.value })}
              />
            </div>
            <div>
              <Label>Price unit</Label>
              <select
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={draft.price_unit}
                onChange={(e) => setDraft({ ...draft, price_unit: e.target.value })}
              >
                <option value="per_person">Per person</option>
                <option value="per_group">Per group</option>
              </select>
            </div>
            <div>
              <Label>Min guests</Label>
              <Input
                type="number"
                min={1}
                value={draft.min_guests}
                onChange={(e) => setDraft({ ...draft, min_guests: +e.target.value })}
              />
            </div>
            <div>
              <Label>Max guests</Label>
              <Input
                type="number"
                min={1}
                value={draft.max_guests}
                onChange={(e) => setDraft({ ...draft, max_guests: +e.target.value })}
              />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                min={30}
                max={360}
                value={draft.duration_minutes}
                onChange={(e) => setDraft({ ...draft, duration_minutes: +e.target.value })}
              />
            </div>
            <div>
              <Label>Sort order</Label>
              <Input
                type="number"
                min={0}
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: +e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Includes (one per line)</Label>
              <Textarea
                rows={4}
                value={draft.includesText}
                onChange={(e) => setDraft({ ...draft, includesText: e.target.value })}
                placeholder={"2 hours karaoke terrace\nSharing platters\n1 pitcher per 6 guests"}
              />
            </div>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input
                type="checkbox"
                checked={draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              />
              Published (visible in the booking form)
            </label>
            <div className="flex gap-2 md:col-span-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save package"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
